import React, { useEffect, useState } from "react";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import { useAlerts } from "../../contexts/AlertsContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import { useApiClient } from "../../contexts/ApiClientContext";
import Country from "../../model/Country";
import { User } from "../../model/User";
import { useNavigate, useParams } from "react-router-dom";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import APIEndPoints from "../../utils/APIEndPoints";
import APIError from "../../utils/APIError";
import FormInputText from "../common/FormInputText";
import FormContainer from "../common/FormContainer";
import UserRoles from "../../model/enums/UserRole";
import SelectWithSearch from "../common/select_search/SelectSearchSide";
import logo from "../resources/images/ChemiNova_logo.png";
import DialogWindowType from "../common/dialog_window/DialogWindowType";

interface IUserFormErrors {
  username: string;
  password: string;
  email: string;
  surname: string;
  name: string;
  country: string;
  affiliation: string;
  profession: string;
  activity_start: string;
}

const defaultFormErrors: IUserFormErrors = {
  username: "",
  password: "",
  email: "",
  surname: "",
  name: "",
  country: "",
  affiliation: "",
  profession: "",
  activity_start: "",
};

export default function SignUp({}: any) {
  const [errorMessage, setErrorMessage] = React.useState("");
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const alertsManager = useAlerts();

  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country>();
  const [filterKeyword, setfilterKeyword] = useState<String>("");

  // user agreement to terms and conditions
  const [agree_tc, setAgreeTC] = useState(false);
  const [agree_cr, setAgreeCR] = useState(false);

  const [loading, setLoading] = useState(false);

  const country_options: { value: string | number; label: string }[] =
    countries.map((c) => ({
      value: c.geonameid,
      label: c.name,
    }));

  const filtered_country_options: { value: string | number; label: string }[] =
    filteredCountries.map((c) => ({
      value: c.geonameid,
      label: c.name,
    }));

  const [formData, setFormData] = useState<User>({} as User);

  const [re_password, setRePassword] = useState<string>("");

  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IUserFormErrors>(defaultFormErrors);

  // Load the list of existing countries
  useEffect(() => {
    const loadCountries = async () => {
      const cntries = await apiClient
        ?.get<Country>({
          url: APIEndPoints.COUNTRIES.ALL,
          requiresToken: false,
        })
        .then((data: any) => {
          console.log("Countries:");
          console.log(data.items);

          setCountries(
            data.items.sort((a: Country, b: Country) =>
              a.name.localeCompare(b.name),
            ),
          );
        })
        .catch((error: IApiClientError) => {
          // check if the error is one of the defaults
          if (!error.processed) {
            setErrorMessage("Could not load countries");
          }
        });
    };

    // set the country options variable for the drop down
    loadCountries();
  }, [apiClient]);

  useEffect(() => {
    if (!filterKeyword) {
      setFilteredCountries([]);
    } else {
      const lower = filterKeyword.toLowerCase();

      setFilteredCountries(
        countries
          .filter((c) => c.name.toLowerCase().includes(lower))
          .slice(0, 5),
      );
    }
  }, [countries, filterKeyword]);

  const formInputChange = (inputID: string, newValue: string) => {
    // Special handling for re-type password field, which is a componentnt member used to validate the passowrd. we only update the value of the component member
    if (inputID === "id_re_password") {
      setRePassword(newValue);

      if (newValue != formData.password) {
        // Show message underneath the re-type password field that the password does not match
        setFormErrors((prevData) => ({
          ...prevData,
          password: "Passwords do not match",
        }));
      } else {
        console.log("Password match!");
        setFormErrors((prevData) => ({
          ...prevData,
          password: "",
        }));
      }
      return;
    }

    // Clear the server error for whichever field the user is editing
    setFormErrors((prevData) => ({ ...prevData, [inputID]: "" }));

    setFormData((prevData) => ({
      ...prevData,
      [inputID]: newValue,
    }));
  };

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormErrors(defaultFormErrors); // Reset errors

    if (!formData.id_country) {
      setFormErrors((prev) => ({
        ...prev,
        country: "Please select a country",
      }));
      return;
    }

    // Make sure to validate password
    if (re_password === formData.password) {
      // Add an active=0 field to form data
      formData.active = 0;
      formData.id_country = formData.id_country;
      formData.role = UserRoles.VISITOR;
      formData.affiliation = "           ";
      formData.profession = "           ";
      formData.activity_start = 2025;
      console.log(formData);

      setLoading(true);

      apiClient
        ?.post({
          url: APIEndPoints.SIGNUP,
          requiresToken: false,
          body: formData,
        })
        .then(() => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "User created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.EMAIL_CONFIRMATION,
              additionalSections: ["registered"],
            }),
          );
          setLoading(false);
        })
        .catch((error: IApiClientError) => {
          setLoading(false);
          // check if the error is one of the defaults
          if (!error.processed) {
            processAPIErrors(error);
          }
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "User registration failed!",
          });
        });
    } else {
      // alert password mismatch
      setErrorMessage("Password mismatch");
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "User registration failed!",
      });
    }
  }

  const search = async (fieldId: string, keyword: string) => {
    if (fieldId === "country") {
      setfilterKeyword(keyword);
      //   setCountries(countriesInfo.items);
    }
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = { ...defaultFormErrors };
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg.replace(
          /^Value error,\s*/i,
          "",
        );
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };
  return (
    <div>
      <section className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="flex flex-col items-center justify-center px-6 mx-auto min-h-screen lg:py-0">
          <img src={logo} className="h-12" alt="" />
          <div className="w-full bg-white rounded-lg shadow dark:border sm:max-w-2xl xl:p-0 pt-0 mt-10">
            <div className="p-6 space-y-4 md:space-y-6 sm:p-8 sm:pt-0">
              <FormContainer
                title={"Create account"}
                onSubmit={(e) => {
                  handleSubmit(e);
                }}
              >
                <FormInputText
                  label="Username"
                  id="username"
                  value={formData.username}
                  required={true}
                  placeholder="Username"
                  onChange={formInputChange}
                  error={formErrors.username}
                />

                <FormInputText
                  label="Email"
                  id="email"
                  value={formData.email}
                  required={true}
                  placeholder="Email"
                  onChange={formInputChange}
                  error={formErrors.email}
                />

                <FormInputText
                  label="Password"
                  id="password"
                  value={formData.password}
                  required={true}
                  placeholder="Password"
                  onChange={formInputChange}
                  error={formErrors.password}
                  type="password"
                />

                <FormInputText
                  label="Re-type Password"
                  id="id_re_password"
                  value={re_password}
                  // required={true}
                  placeholder="Password"
                  onChange={formInputChange}
                  error={formErrors.password}
                  type="password"
                />

                <FormInputText
                  label="Name"
                  id="name"
                  value={formData.name}
                  required={true}
                  placeholder="Name"
                  onChange={formInputChange}
                  error={formErrors.name}
                />

                <FormInputText
                  label="Surname"
                  id="surname"
                  value={formData.surname}
                  required={true}
                  placeholder="Surname"
                  onChange={formInputChange}
                  error={formErrors.surname}
                />

                <SelectWithSearch
                  id="country"
                  label="Country"
                  value={formData.id_country}
                  noValueMessage={selectedCountry?.name ?? "-"}
                  options={filtered_country_options}
                  onChange={(
                    inputID: string,
                    newValue: number | string | undefined,
                  ) => {
                    const found = countries.find(
                      (c) => c.geonameid === newValue,
                    );
                    if (found) {
                      setSelectedCountry(found);
                      setFormData((prevData) => ({
                        ...prevData,
                        id_country: found.geonameid,
                      }));
                      console.log(formData.id_country);
                    }
                  }}
                  onSearch={search}
                  showClearFilter={false}
                  topCSS={"select-search-addedit"}
                />
                {formErrors.country && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.country}
                  </p>
                )}

                {/* License Agreement group */}
                <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
                  <label className="flex items-center gap-2 text-sm mt-1">
                    <input
                      type="checkbox"
                      checked={agree_tc}
                      onChange={(e) => setAgreeTC(e.target.checked)}
                    />
                    <span>
                      I agree with the{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          alertsManager.showDialogWindow({
                            title: "",
                            type: DialogWindowType.LICENSE,
                            message: "terms-and-conditions",
                          });
                        }}
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Terms and Conditions
                      </a>
                    </span>
                  </label>

                  <label className="flex items-center gap-2 text-sm mt-1">
                    <input
                      type="checkbox"
                      checked={agree_cr}
                      onChange={(e) => setAgreeCR(e.target.checked)}
                    />
                    <span>
                      I agree with the{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          alertsManager.showDialogWindow({
                            title: "",
                            type: DialogWindowType.LICENSE,
                            message: "community-rules",
                          });
                        }}
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Community Rules
                      </a>
                    </span>
                  </label>
                </div>

                <div className="form-buttons-container">
                  <Button
                    label="Cancel"
                    purpose={ButtonAction.CANCEL}
                    onClick={() => navigate("/")}
                    className="w-full text-white bg-red-500 hover:bg-red-600 focus:ring-4 focus:outline-none focus:ring-red-300"
                  />
                  <Button
                    label={"Create Account"}
                    purpose={ButtonAction.SAVE}
                    isSubmit={true}
                    // onClick={handleSubmit}
                    className="w-full"
                    disabled={(!agree_tc || !agree_cr) && !loading}
                  />
                </div>

                {loading && (
                  <div>Your account is being created. Please wait...</div>
                )}
              </FormContainer>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
